import React, {
	Fragment,
	useCallback,
	useEffect,
	useRef,
	useState,
} from 'react';
import update from 'immutability-helper';
import { Routes, useNavigate, useParams } from 'react-router-dom';
import {
	CheckCircleIcon,
	EllipsisVerticalIcon,
	PhotoIcon,
	PlusCircleIcon,
	PlusIcon,
	TrashIcon,
	XCircleIcon,
} from '@heroicons/react/24/outline';
import { ChevronLeftIcon } from '@heroicons/react/20/solid';
import { Menu, Transition } from '@headlessui/react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import moment from 'moment';

import { classNames } from '../components/helpers/functions';
import DataTable from '../components/DataTable';
import { MainContent, PageHeading } from '../components/Shell/Content';

import useAsync from '../hooks/useAsync';

import {
	createAdminServiceBlocks,
	createBlankAdminServiceReport,
	deleteAdminServiceBlock,
	getAdminServiceReport,
	getAllAdminServiceBlocks,
	getAllAdminServiceReports,
	getAssetsAdmin,
	getCustomers,
	updateAdminServiceBlocks,
	updateAdminServiceReport,
	uploadServiceReportImages,
} from '../api/admin';

import Logo from '../images/logo-mark.png';

import Tabs from '../components/Tabs';
import ProtectedRoute from '../components/helpers/ProtectedRoute';
import Modal from '../components/Shell/Modal';
import {
	TemplateCreateable,
	TemplateDate,
	TemplateEditor,
	TemplateField,
	TemplateReactSelect,
} from '../components/Attributes/Attributes_old';
import { Button } from '../components/Button';
import LoadingSkeleton from '../components/Shell/LoadingSkeleton';

import useModal from '../hooks/useModal';
import usePrevious from '../hooks/usePrevious';

import { Block } from './ServiceReports/Block';

const BLOCK_TYPES = [
	{ label: 'Repair', value: 'repair' },
	{ label: 'Preventive Maintenance', value: 'pm' },
	{ label: 'Notes', value: 'notes' },
	{ label: 'Parts Replaced', value: 'parts' },
];

const DEFAULT_FORM = {
	completedOn: new Date(),
};

const ServiceReportsList = () => {
	const [newBlockForm, setNewBlockForm] = useState({});
	const navigate = useNavigate();
	const [currentTab, setCurrentTab] = useState('all');
	const [form, setForm] = useState(DEFAULT_FORM);
	const [isCreateServiceReportOpen, setCreateServiceReportOpen] =
		useState(false);

	const {
		open: isNewBlockModalOpen,
		setOpen: setNewBlockModalOpen,
		setClose: setNewBlockModalClose,
	} = useModal('addContentBlock');

	const {
		open: isAddOrganizationModalOpen,
		setOpen: onOpenAddOrganizationModal,
	} = useModal('addOrganization');

	const {
		open: isAddAssetModalOpen,
		setOpen: onOpenAddAssetToOrganizationModal,
	} = useModal('addAssetToOrganization');

	const documents = { rows: [] };

	const { value: serviceReports } = useAsync(getAllAdminServiceReports);
	const { value: customers, execute: onRefreshCustomers } =
		useAsync(getCustomers);
	const { value: serviceBlocks, execute: onRefreshServiceBlocks } = useAsync(
		getAllAdminServiceBlocks,
		{
			defaultPayload: { predefined: true },
		},
	);

	const {
		value: assets,
		execute: onRefreshAssets,
		resetValue: resetAssets,
	} = useAsync(getAssetsAdmin, { immediate: false });
	const { execute: onCreateServiceReport } = useAsync(
		createBlankAdminServiceReport,
		{ immediate: false },
	);

	const { execute: onDeleteServiceBlock } = useAsync(deleteAdminServiceBlock, {
		immediate: false,
	});

	const prevAddOrganization = usePrevious(isAddOrganizationModalOpen);
	useEffect(() => {
		if (!isAddOrganizationModalOpen && prevAddOrganization) {
			onRefreshCustomers();
		}
	}, [isAddOrganizationModalOpen]);

	useEffect(() => {
		if (form?.customer) {
			onRefreshAssets(form?.customer);
		} else {
			resetAssets();
		}
	}, [form?.customer]);

	const prevAddAssetModalOpen = usePrevious(isAddAssetModalOpen);
	useEffect(() => {
		if (!isAddAssetModalOpen && prevAddAssetModalOpen) {
			onRefreshAssets(form?.customer);
		}
	}, [isAddAssetModalOpen]);

	const prevCreateReportModalOpen = usePrevious(isCreateServiceReportOpen);
	useEffect(() => {
		if (!isCreateServiceReportOpen && prevCreateReportModalOpen) {
			setForm(DEFAULT_FORM);
		}
	}, [isCreateServiceReportOpen]);

	const prevNewBlockModal = usePrevious(isNewBlockModalOpen);
	useEffect(() => {
		if (!isNewBlockModalOpen && prevNewBlockModal) {
			onRefreshServiceBlocks({ predefined: true });
			setNewBlockForm({});
		}
	}, [isNewBlockModalOpen]);

	useEffect(() => {
		if (isNewBlockModalOpen?.block?.id) {
			setNewBlockForm(isNewBlockModalOpen?.block);
		}
	}, [isNewBlockModalOpen?.block?.id]);

	const CUSTOMER_OPTIONS = customers?.map((customer) => ({
		label: customer?.name,
		value: customer?.id,
	}));

	const ASSET_OPTIONS = assets?.map((asset) => ({
		label: `${asset?.manufacturer?.name} ${asset?.model?.name} (S/N: ${asset?.serialNumber})`,
		value: asset?.id,
		asset,
	}));

	const TYPE_OPTIONS = [
		{ label: 'Repair', value: 'repair' },
		{ label: 'Preventive Maintenance', value: 'pm' },
		{ label: 'Inspection', value: 'inspection' },
	];

	const selectedAsset =
		ASSET_OPTIONS?.find((o) => o.value === form?.asset) || null;

	return (
		<MainContent>
			<PageHeading
				divider={false}
				actions={[
					{
						name: 'Create new',
						onClick: () => setCreateServiceReportOpen(true),
					},
				]}
			>
				Service Reports
			</PageHeading>
			<Tabs
				activeTab={'all'}
				tabs={[
					{ name: 'All Service Reports', key: 'all' },
					{ name: 'Blocks', key: 'blocks' },
				]}
			>
				{(tab) => {
					if (tab === 'all') {
						return (
							<div className="grid grid-cols-12">
								<div className="col-span-3">
									<nav
										className={classNames('flex flex-1 flex-col mt-4')}
										aria-label="Sidebar"
									>
										<ul role="list" className="-mx-2 space-y-1">
											{[
												{
													name: 'All',
													onClick: () => {
														handleTabChange('all');
														//navigate({ search: '' });
													},
													current: currentTab === 'all' || !currentTab,
													count: documents?.absoluteTotal,
												},
											].map((item) => {
												return (
													<li key={item.name}>
														<div
															onClick={() => item.onClick()}
															className={classNames(
																item.current
																	? 'bg-gray-50 text-indigo-600'
																	: 'text-gray-700 hover:text-indigo-600 hover:bg-gray-50',
																'group flex gap-x-3 rounded-md p-2 text-xs leading-6 font-medium cursor-pointer',
															)}
														>
															{item.icon && (
																<item.icon
																	className={classNames(
																		item.current
																			? 'text-indigo-600'
																			: 'text-gray-400 group-hover:text-indigo-600',
																		'h-6 w-6 shrink-0',
																	)}
																	aria-hidden="true"
																/>
															)}
															{item.name}
															{typeof item.count !== 'undefined' ? (
																<span
																	className="ml-auto w-9 min-w-max whitespace-nowrap rounded-full bg-white px-2.5 py-0.5 text-center text-xxs font-medium leading-5 text-gray-600 ring-1 ring-inset ring-gray-200"
																	aria-hidden="true"
																>
																	{item.count || 0}
																</span>
															) : null}
														</div>
													</li>
												);
											})}
										</ul>
									</nav>
								</div>
								<div className="col-span-9 mx-2 px-4 mt-4">
									<DataTable
										condensed
										columns={[
											{
												header: 'Name',
												accessorKey: 'name',
												cell: (info) =>
													info?.row?.original?.loading
														? 'Uploading... '
														: info?.getValue() || '',
											},

											{
												header: 'Status',
												accessorKey: 'status',
												cell: (info) => info?.getValue() || '',
											},
											{
												header: 'Invoice',
												accessorKey: 'invoiceNumber',
												cell: (info) => info?.getValue() || '',
											},
											{
												header: 'Type',
												accessorKey: 'type',
												cell: (info) => info?.getValue() || '',
											},
											{
												header: 'Completed On',
												accessorKey: 'completedOn',
												cell: (info) =>
													info?.getValue()
														? moment(info?.getValue()).format('MM/DD/YYYY')
														: '',
											},
										]}
										data={{ rows: serviceReports }}
										usePagination={false}
										useExport={false}
										hasCheckboxes={false}
										manualSorting={true}
										onRow={(row) =>
											navigate(`/admin/service-report/${row?.original?.id}`)
										}
									/>
								</div>
							</div>
						);
					} else if (tab === 'blocks') {
						return (
							<>
								<DataTable
									title="All Blocks"
									onRow={(row) =>
										setNewBlockModalOpen({ block: row?.original })
									}
									rowActions={[
										{
											name: 'Delete Block',
											danger: 'Are you sure you want to delete this block?',
											confirmText: 'Delete block',
											onClick: (row) =>
												onDeleteServiceBlock({ id: row?.original?.id }, () =>
													onRefreshServiceBlocks({ predefined: true }),
												),
										},
									]}
									columns={[
										{
											header: 'Name',
											accessorKey: 'name',
											cell: (info) => info?.getValue() || '',
										},
										{
											header: 'Type',
											accessorKey: 'type',
											cell: (info) => info?.getValue() || '',
										},
										{
											header: 'Content',
											accessorKey: 'content',
											cell: (info) => info?.getValue()?.substring(0, 100) || '',
										},
									]}
									actions={[
										{
											label: 'Add new block',
											type: 'secondary',
											onClick: () => setNewBlockModalOpen(true),
										},
									]}
									usePagination={false}
									useExport={false}
									hasCheckboxes={false}
									data={{ rows: serviceBlocks || [] }}
								/>
							</>
						);
					}
				}}
			</Tabs>

			<Modal
				title="Create a new service report"
				open={isCreateServiceReportOpen}
				onClose={() => setCreateServiceReportOpen(false)}
			>
				<div className="grid grid-cols-2 gap-y-4">
					{' '}
					<div className={classNames('sm:col-span-2 flex h-fit')}>
						<div className="flex-1">
							<TemplateReactSelect
								name={'Customer'}
								dataIndex="customer"
								options={CUSTOMER_OPTIONS}
								value={
									CUSTOMER_OPTIONS?.find((o) => o.value === form?.customer) ||
									null
								}
								onChange={(index, value) =>
									setForm({
										...form,
										[index]: value?.value,
										invoiceNumber: value?.value
											? `${value?.label
													?.substring(0, 3)
													.toUpperCase()}-${Math.floor(
													100000 + Math.random() * 900000,
												)}`
											: '',
									})
								}
							/>
						</div>
						{form['customer'] ? (
							<div className="flex-shrink flex items-center pt-7 ml-3">
								<XCircleIcon
									className="w-6 h-6 cursor-pointer text-gray-500"
									onClick={() =>
										setForm({ ...form, customer: null, invoiceNumber: '' })
									}
								/>
							</div>
						) : (
							<div className="flex-shrink flex items-center pt-7 ml-3">
								<PlusCircleIcon
									className="w-6 h-6 cursor-pointer text-gray-500"
									onClick={() => onOpenAddOrganizationModal()}
								/>
							</div>
						)}
					</div>
					<div className={classNames('sm:col-span-2 flex h-fit')}>
						<div className="flex-1">
							<TemplateReactSelect
								name="Asset"
								disabled={!form?.customer}
								options={ASSET_OPTIONS}
								value={selectedAsset}
								onChange={(index, asset) => {
									const reportName = `${
										CUSTOMER_OPTIONS?.find((o) => o.value === form?.customer)
											?.label
									} ${asset?.asset?.manufacturer?.name} ${
										asset?.asset?.model?.name
									}`;

									setForm({
										...form,
										asset: asset?.value,
										name: reportName,
										fileName: `Scipods - ${reportName} - ${moment().format(
											'MMDDYYYY',
										)}.pdf`,
									});
								}}
							/>
						</div>
						{form['asset'] ? (
							<div className="flex-shrink flex items-center pt-7 ml-3">
								<XCircleIcon
									className="w-6 h-6 cursor-pointer text-gray-500"
									onClick={() =>
										setForm({ ...form, asset: null, name: '', fileName: '' })
									}
								/>
							</div>
						) : form['customer'] ? (
							<div className="flex-shrink flex items-center pt-7 ml-3">
								<PlusCircleIcon
									className="w-6 h-6 cursor-pointer text-gray-500"
									onClick={() =>
										onOpenAddAssetToOrganizationModal({
											organization: CUSTOMER_OPTIONS?.find(
												(o) => o.value === form?.customer,
											),
											callback: (payload) => {
												const reportName = `${
													CUSTOMER_OPTIONS?.find(
														(o) => o.value === form?.customer,
													)?.label
												} ${payload?.manufacturer?.name} ${
													payload?.model?.name
												}`;
												setForm({
													...form,
													asset: payload?.id,
													name: reportName,
													fileName: `Scipods - ${reportName} - ${moment().format(
														'MMDDYYYY',
													)}.pdf`,
												});
											},
										})
									}
								/>
							</div>
						) : null}
					</div>
					<TemplateField
						name="Name"
						value={form?.name}
						onChange={(index, name) => setForm({ ...form, name })}
					/>
					<TemplateField
						name="File Name"
						value={form?.fileName}
						onChange={(index, fileName) => setForm({ ...form, fileName })}
					/>
					<TemplateDate
						name="Completed on"
						value={form?.completedOn}
						onChange={(index, completedOn) => setForm({ ...form, completedOn })}
					/>
					<TemplateReactSelect
						name="Type"
						options={TYPE_OPTIONS}
						value={TYPE_OPTIONS?.find((o) => o.value === form?.type) || null}
						onChange={(index, type) => setForm({ ...form, type: type?.value })}
					/>
					<TemplateField
						name="Invoice Number"
						value={form?.invoiceNumber}
						onChange={(index, invoiceNumber) =>
							setForm({ ...form, invoiceNumber })
						}
					/>
					<div className="flex justify-end col-span-2">
						<Button
							onClick={() => {
								onCreateServiceReport(
									{
										name: form?.name,
										asset: form?.asset,
										organizationId: form?.customer,
										fileName: form?.fileName,
										completedOn: form?.completedOn,
										type: form?.type,
										invoiceNumber: form?.invoiceNumber,
									},
									(payload) => {
										navigate(`/admin/service-report/${payload?.id}`);
										setCreateServiceReportOpen(false);
									},
								);
							}}
						>
							Create
						</Button>
					</div>
				</div>
			</Modal>
		</MainContent>
	);
};

const ServiceReportsSingle = () => {
	const navigate = useNavigate();
	const { id } = useParams();

	const { open: isNewBlockModalOpen, setOpen: setNewBlockModalOpen } =
		useModal('addContentBlock');

	const [newBlockForm, setNewBlockForm] = useState({});

	const [isAddContentBlockOpen, setAddContentBlockOpen] = useState(false);
	const [isAddPredefinedContentBlockOpen, setAddPredefinedContentBlockOpen] =
		useState(false);

	const [contentBlockForm, setContentBlockForm] = useState({});

	const {
		value: serviceReport,
		loading: isLoadingServiceReport,
		execute: onRefreshServiceReport,
	} = useAsync(getAdminServiceReport, { defaultPayload: { id } });

	const { value: organizations } = useAsync(getCustomers);

	const { execute: onUpdateServiceReport } = useAsync(
		updateAdminServiceReport,
		{ immediate: false },
	);

	const { execute: onCreateServiceBlock } = useAsync(createAdminServiceBlocks, {
		immediate: false,
	});

	const { value: serviceBlocks, execute: onRefreshServiceBlocks } = useAsync(
		getAllAdminServiceBlocks,
		{
			defaultPayload: { predefined: true },
		},
	);

	const { execute: onUpdateServiceBlock } = useAsync(updateAdminServiceBlocks, {
		immediate: false,
	});

	const { execute: onDeleteServiceBlock } = useAsync(deleteAdminServiceBlock, {
		immediate: false,
	});

	const { execute: onUploadImages, loading: isUploadingImages } = useAsync(
		uploadServiceReportImages,
		{ immediate: false },
	);

	const [images, setImages] = useState([]);
	const fileInputRef = useRef(null);

	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

	const [form, setForm] = useState({});
	const [addLineModalOpen, setAddLineModalOpen] = useState(false);
	const [details, setDetails] = useState([]);
	const [line, setLine] = useState({});

	const [cards, setCards] = useState([]);

	const [editingElement, setEditing] = useState(null);

	const prevContentBlockOpen = usePrevious(isAddContentBlockOpen);
	useEffect(() => {
		if (!isAddContentBlockOpen && prevContentBlockOpen) {
			setContentBlockForm({});
			onRefreshServiceReport({ id });
		}
	}, [isAddContentBlockOpen]);

	const prevNewBlockModal = usePrevious(isNewBlockModalOpen);
	useEffect(() => {
		if (!isNewBlockModalOpen && prevNewBlockModal) {
			onRefreshServiceReport({ id });
		}
	}, [isNewBlockModalOpen]);

	useEffect(() => {
		if (serviceReport?.id) {
			if (serviceReport?.blockOrder?.length && serviceReport?.blocks) {
				setCards(
					serviceReport?.blockOrder?.map((blockId) =>
						serviceReport?.blocks?.find((o) => o.id === blockId),
					),
				);
			} else {
				setCards(serviceReport?.blocks || []);
			}
			// Initialize images from service report
			if (serviceReport?.images) {
				setImages(serviceReport.images);
			}
		}
	}, [serviceReport?.id]);

	const handleImageUpload = (event) => {
		const files = event.target.files;
		if (!files || files.length === 0) return;

		onUploadImages({ id, files: Array.from(files) }, (response) => {
			if (response?.files) {
				const newImages = [...images, ...response.files];
				setImages(newImages);
				// Save images to service report
				onUpdateServiceReport(
					{
						id,
						key: 'images',
						value: newImages,
					},
					() => onRefreshServiceReport({ id }),
				);
			}
		});

		// Reset the file input
		if (fileInputRef.current) {
			fileInputRef.current.value = '';
		}
	};

	const handleRemoveImage = (imageIndex) => {
		const newImages = images.filter((_, index) => index !== imageIndex);
		setImages(newImages);
		onUpdateServiceReport(
			{
				id,
				key: 'images',
				value: newImages,
			},
			() => onRefreshServiceReport({ id }),
		);
	};

	const handleSavePDF = async () => {
		const element = document.getElementById('invoice-content');
		if (!element) return;

		// Letter size in mm (8.5 x 11 inches)
		const pageWidth = 215.9;
		const pageHeight = 279.4;
		const margin = 15;

		// Target width in pixels for letter-size content (7.5 inches at 96 DPI)
		const targetWidth = 920;

		const canvas = await html2canvas(element, {
			scale: 2,
			useCORS: true,
			allowTaint: true,
			backgroundColor: '#ffffff',
			width: targetWidth,
			onclone: (clonedDoc) => {
				// Set fixed width on cloned element for consistent PDF sizing
				const clonedElement = clonedDoc.getElementById('invoice-content');
				if (clonedElement) {
					clonedElement.style.width = `${targetWidth}px`;
					clonedElement.style.maxWidth = `${targetWidth}px`;
					clonedElement.style.padding = '20px';
					clonedElement.style.boxSizing = 'border-box';
				}

				// Fix oklch/oklab colors (unsupported by html2canvas)
				const resolveColor = (value) => {
					if (
						!value ||
						(!value.includes('oklab') && !value.includes('oklch'))
					) {
						return null;
					}
					const c = document.createElement('canvas');
					c.width = c.height = 1;
					const ctx = c.getContext('2d');
					ctx.fillStyle = value;
					ctx.fillRect(0, 0, 1, 1);
					const [r, g, b, a] = ctx.getImageData(0, 0, 1, 1).data;
					return a === 255
						? `rgb(${r},${g},${b})`
						: `rgba(${r},${g},${b},${a / 255})`;
				};

				clonedDoc.querySelectorAll('*').forEach((el) => {
					const styles = getComputedStyle(el);
					['color', 'backgroundColor', 'borderColor'].forEach((prop) => {
						const rgb = resolveColor(styles[prop]);
						if (rgb) el.style[prop] = rgb;
					});
				});
			},
		});

		const imgData = canvas.toDataURL('image/jpeg', 0.95);
		const imgWidth = pageWidth - margin * 2;
		const imgHeight = (canvas.height * imgWidth) / canvas.width;
		const pageContentHeight = pageHeight - margin * 2;

		const pdf = new jsPDF('p', 'mm', 'letter');
		let position = margin;
		let heightLeft = imgHeight;

		pdf.addImage(imgData, 'JPEG', margin, position, imgWidth, imgHeight);
		heightLeft -= pageContentHeight;

		while (heightLeft > 0) {
			position = heightLeft - imgHeight + margin;
			pdf.addPage();
			pdf.addImage(imgData, 'JPEG', margin, position, imgWidth, imgHeight);
			heightLeft -= pageContentHeight;
		}

		const fileName =
			serviceReport?.fileName ||
			`service-report-${serviceReport?.invoiceNumber || 'export'}.pdf`;
		pdf.save(fileName.endsWith('.pdf') ? fileName : `${fileName}.pdf`);
	};

	const moveCard = useCallback((dragIndex, hoverIndex) => {
		setCards((prevCards) => {
			const updated = update(prevCards, {
				$splice: [
					[dragIndex, 1],
					[hoverIndex, 0, prevCards[dragIndex]],
				],
			});

			onUpdateServiceReport({
				id,
				key: 'blockOrder',
				value: updated?.map((card) => card?.id),
			});

			return updated;
		});
	}, []);
	const renderCard = useCallback((card, index, serviceReport) => {
		if (!card) {
			return null;
		}
		return (
			<Block
				key={card.id}
				index={index}
				id={card.id}
				block={card}
				text={card.name}
				moveCard={moveCard}
				serviceReport={serviceReport}
				onEdit={(row) => setNewBlockModalOpen({ block: row })}
				onDelete={(row, callback) =>
					onDeleteServiceBlock({ id: row?.id }, () => {
						if (typeof callback === 'function') {
							callback();
						}
						onRefreshServiceReport({ id });
					})
				}
			/>
		);
	}, []);

	return (
		<>
			<MainContent maxWidth="w-full" className="sm:px-0 lg:px-0 py-0 lg:py-0">
				{isLoadingServiceReport === 'pending' ? (
					<LoadingSkeleton />
				) : (
					<>
						<header className="relative isolate">
							<div
								className="absolute inset-0 -z-10 overflow-hidden"
								aria-hidden="true"
							>
								<div className="absolute left-16 top-full -mt-16 transform-gpu opacity-50 blur-3xl xl:left-1/2 xl:-ml-80">
									<div
										className="aspect-[1154/678] w-[72.125rem] bg-gradient-to-br from-[#FF80B5] to-[#9089FC]"
										style={{
											clipPath:
												'polygon(100% 38.5%, 82.6% 100%, 60.2% 37.7%, 52.4% 32.1%, 47.5% 41.8%, 45.2% 65.6%, 27.5% 23.4%, 0.1% 35.3%, 17.9% 0%, 27.7% 23.4%, 76.2% 2.5%, 74.2% 56%, 100% 38.5%)',
										}}
									/>
								</div>
								<div className="absolute inset-x-0 bottom-0 h-px bg-gray-900/5" />
							</div>

							<div className="mx-auto px-4 py-10 sm:px-6 lg:px-8">
								<div className="mx-auto flex max-w-2xl items-center justify-between gap-x-8 lg:mx-0 lg:max-w-none">
									<div className="flex items-center gap-x-6">
										<ChevronLeftIcon
											className="w-5 h-5 text-gray-500 cursor-pointer"
											onClick={() => navigate(-1)}
										/>
										<h1>
											<div className="text-sm leading-6 text-gray-500">
												Invoice{' '}
												<span className="text-gray-700">
													#{serviceReport?.invoiceNumber}
												</span>
											</div>
											<div className="mt-1 text-base font-semibold leading-6 text-gray-900">
												{serviceReport?.organization?.name}
											</div>
										</h1>
									</div>
									<div className="flex items-center gap-x-4 sm:gap-x-6">
										<Button onClick={handleSavePDF}>Download PDF</Button>

										<Menu as="div" className="relative sm:hidden">
											<Menu.Button className="-m-3 block p-3">
												<span className="sr-only">More</span>
												<EllipsisVerticalIcon
													className="h-5 w-5 text-gray-500"
													aria-hidden="true"
												/>
											</Menu.Button>

											<Transition
												as={Fragment}
												enter="transition ease-out duration-100"
												enterFrom="transform opacity-0 scale-95"
												enterTo="transform opacity-100 scale-100"
												leave="transition ease-in duration-75"
												leaveFrom="transform opacity-100 scale-100"
												leaveTo="transform opacity-0 scale-95"
											>
												<Menu.Items className="absolute right-0 z-10 mt-0.5 w-32 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none">
													<Menu.Item>
														{({ active }) => (
															<button
																type="button"
																className={classNames(
																	active ? 'bg-gray-50' : '',
																	'block w-full px-3 py-1 text-left text-sm leading-6 text-gray-900',
																)}
															>
																Copy URL
															</button>
														)}
													</Menu.Item>
													<Menu.Item>
														{({ active }) => (
															<a
																href="#"
																className={classNames(
																	active ? 'bg-gray-50' : '',
																	'block px-3 py-1 text-sm leading-6 text-gray-900',
																)}
															>
																Edit
															</a>
														)}
													</Menu.Item>
												</Menu.Items>
											</Transition>
										</Menu>
									</div>
								</div>
							</div>
						</header>

						<div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
							<div className="mx-auto grid max-w-2xl grid-cols-1 grid-rows-1 items-start gap-x-8 gap-y-8 lg:mx-0 lg:max-w-none ">
								{/* Invoice summary */}
								{/*<div className="lg:col-start-3 lg:row-end-1">
									<div className="rounded-lg bg-gray-50 shadow-sm ring-1 ring-gray-900/5">
										<dl className="flex flex-wrap">
											<div className="flex-none self-end px-6 pt-4">
												<dt className="sr-only">Status</dt>
												<dd className="rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-600 ring-1 ring-inset ring-green-600/20">
													Paid
												</dd>
											</div>
											<div className="mt-6 flex w-full flex-none gap-x-4 border-t border-gray-900/5 px-6 pt-6">
												<dt className="flex-none">
													<span className="sr-only">Client</span>
													<UserCircleIcon
														className="h-6 w-5 text-gray-400"
														aria-hidden="true"
													/>
												</dt>
												<dd className="text-sm font-medium leading-6 text-gray-900">
													Alex Curren
												</dd>
											</div>
											<div className="mt-4 flex w-full flex-none gap-x-4 px-6">
												<dt className="flex-none">
													<span className="sr-only">Due date</span>
													<CalendarDaysIcon
														className="h-6 w-5 text-gray-400"
														aria-hidden="true"
													/>
												</dt>
												<dd className="text-sm leading-6 text-gray-500">
													<time dateTime="2023-01-31">January 31, 2023</time>
												</dd>
											</div>
											<div className="mt-4 flex w-full flex-none gap-x-4 px-6">
												<dt className="flex-none">
													<span className="sr-only">Status</span>
													<CreditCardIcon
														className="h-6 w-5 text-gray-400"
														aria-hidden="true"
													/>
												</dt>
												<dd className="text-sm leading-6 text-gray-500">
													Paid with MasterCard
												</dd>
											</div>
										</dl>
										<div className="mt-6 border-t border-gray-900/5 px-6 py-6">
											<a
												href="#"
												className="text-sm font-semibold leading-6 text-gray-900"
											>
												Download receipt <span aria-hidden="true">&rarr;</span>
											</a>
										</div>
									</div>
																</div>*/}

								{/* Invoice */}
								<div
									id="invoice-content"
									className="-mx-4 px-4 py-8 yarn d sm:mx-0 sm:rounded-lg sm:px-8 sm:pb-14 lg:col-span-2 lg:row-span-2 lg:row-end-2 xl:px-16 xl:pb-20 xl:pt-16"
								>
									<div className="-mx-4 sm:mx-0 lg:col-span-2 lg:row-span-2 lg:row-end-2">
										<div className="flex justify-between">
											<div className="flex items-center">
												<img src={Logo} className="h-16 rounded mr-6" />
												<div>
													<h2 className="text-xl font-semibold leading-6 text-gray-900">
														Service Report
													</h2>
													<div className="text-gray-500 text-xs">
														{serviceReport?.invoiceNumber}
													</div>
												</div>
											</div>

											<div className="sm:pr-4 text-sm leading-6 flex items-center">
												<dt className="inline text-gray-500 mr-2">Date:</dt>{' '}
												<dd className="inline text-gray-700">
													<TemplateDate
														isEditing={editingElement === 'date'}
														value={
															editingElement === 'date'
																? moment(serviceReport?.date).toDate()
																: moment(serviceReport?.date).format(
																		'MM/DD/YYYY',
																	)
														}
														onFocus={() => setEditing('date')}
														onBlur={() => setEditing(null)}
														onChange={(index, date) => {
															onUpdateServiceReport(
																{
																	id: serviceReport?.id,
																	key: 'date',
																	value: date,
																},
																() => {
																	setEditing(null);
																	onRefreshServiceReport({ id });
																},
															);
														}}
													/>
												</dd>
											</div>
										</div>
										<dl className="mt-6 grid grid-cols-1 text-sm leading-6 sm:grid-cols-2">
											<div className="mt-6 border-t border-gray-900/5 pt-6 sm:pr-4">
												<dt className="text-gray-600 text-xs">Service by:</dt>
												<dd className="mt-2 text-gray-500">
													<span className="font-medium text-gray-900">
														Cezary Kubel
													</span>
													<br />
													cezary@scipods.com
													<br />
													978-239-8183
												</dd>
												<dd className="mt-2 text-gray-500">
													<span className="font-medium text-gray-900">
														Scipods
													</span>
													<br />
													100 Cummings Center, Suite 218D
													<br />
													Beverly, MA 01915
												</dd>
											</div>
											<div className="mt-6 border-t border-gray-900/5 pt-6 sm:pr-4">
												<dt className="text-gray-600 text-xs">Customer:</dt>
												<dd className="mt-2 text-gray-500">
													<span className="font-medium text-gray-900">
														{serviceReport?.customerName ||
															serviceReport?.organization?.name}
													</span>
													<br />
													<div
														dangerouslySetInnerHTML={{
															__html:
																serviceReport?.customerAddress ||
																serviceReport?.organization?.address ||
																'',
														}}
														style={{ whiteSpace: 'pre-wrap' }}
													/>
													<div data-html2canvas-ignore={true} className="mt-3">
														<TemplateReactSelect
															name="Override Customer"
															placeholder="Select organization..."
															options={organizations?.map((org) => ({
																label: org?.name,
																value: org?.id,
																address: org?.address,
															}))}
															onChange={(index, selected) => {
																if (selected) {
																	onUpdateServiceReport(
																		{
																			id: serviceReport?.id,
																			key: 'customerName',
																			value: selected.label,
																		},
																		() => {
																			if (selected.address) {
																				onUpdateServiceReport(
																					{
																						id: serviceReport?.id,
																						key: 'customerAddress',
																						value: selected.address,
																					},
																					() => onRefreshServiceReport({ id }),
																				);
																			} else {
																				onRefreshServiceReport({ id });
																			}
																		},
																	);
																}
															}}
														/>
													</div>
												</dd>
											</div>
										</dl>

										<table className="mt-16 w-fulltext-left text-sm leading-6">
											<colgroup>
												<col className="w-full" />
												<col />
												<col />
												<col />
											</colgroup>
											<thead className="border-b border-gray-200 text-gray-900">
												<tr>
													<th
														scope="col"
														className="px-0 py-3 font-semibold"
													></th>
													<th
														scope="col"
														className="hidden py-3 pl-8 pr-0 font-semibold sm:table-cell"
													></th>
												</tr>
											</thead>
											<tbody>
												<tr>
													<td className="max-w-0 px-0 py-5 align-top">
														<div className="truncate font-medium text-gray-900">
															{`${serviceReport?.asset?.manufacturer?.name} ${serviceReport?.asset?.model?.name}`}
														</div>
														<div className="truncate text-gray-500">{`S/N: ${serviceReport?.asset?.serialNumber}`}</div>
													</td>

													<td className="hidden py-5 pl-8 pr-0 align-top tabular-nums text-gray-700 sm:table-cell  whitespace-nowrap align-middle	">
														<div className="sm:pr-4 text-sm leading-6 flex items-center">
															<dt className="inline text-gray-500 mr-2">
																Completed on{' '}
															</dt>{' '}
															<dd className="inline text-gray-700 min-w-36">
																<TemplateDate
																	isEditing={editingElement === 'completedOn'}
																	value={
																		editingElement === 'completedOn'
																			? moment(
																					serviceReport?.completedOn,
																				).toDate()
																			: moment(
																					serviceReport?.completedOn,
																				).format('MM/DD/YYYY')
																	}
																	onFocus={() => setEditing('completedOn')}
																	onBlur={() => setEditing(null)}
																	onChange={(index, completedOn) => {
																		onUpdateServiceReport(
																			{
																				id: serviceReport?.id,
																				key: 'completedOn',
																				value: completedOn,
																			},
																			() => {
																				setEditing(null);
																				onRefreshServiceReport({ id });
																			},
																		);
																	}}
																/>
															</dd>
														</div>
													</td>
												</tr>

												<>
													{cards.map((card, i) =>
														renderCard(card, i, serviceReport),
													)}
												</>

												<tr data-html2canvas-ignore={true}>
													<td colSpan={2}>
														<Menu as="div" className="relative">
															<Menu.Button className="block bg-gray-50 py-4 px-6 rounded w-full">
																<div className="flex items-center justify-center cursor-pointer text-xs text-gray-500">
																	<PlusIcon className="w-4 h-4 text-gray-400 mr-3" />{' '}
																	Add Content Block
																</div>
															</Menu.Button>

															<Transition
																as={Fragment}
																enter="transition ease-out duration-100"
																enterFrom="transform opacity-0 scale-95"
																enterTo="transform opacity-100 scale-100"
																leave="transition ease-in duration-75"
																leaveFrom="transform opacity-100 scale-100"
																leaveTo="transform opacity-0 scale-95"
															>
																<Menu.Items className="absolute right-0 z-10 mt-0.5 w-64 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none">
																	<Menu.Item>
																		{({ active }) => (
																			<button
																				type="button"
																				className={classNames(
																					active ? 'bg-gray-50' : '',
																					'block w-full px-3 py-1 text-left text-sm leading-6 text-gray-900',
																				)}
																				onClick={() =>
																					setAddContentBlockOpen(true)
																				}
																			>
																				New Block
																			</button>
																		)}
																	</Menu.Item>
																	<Menu.Item>
																		{({ active }) => (
																			<a
																				className={classNames(
																					active ? 'bg-gray-50' : '',
																					'block px-3 py-1 text-sm leading-6 text-gray-900',
																				)}
																				onClick={() =>
																					setAddPredefinedContentBlockOpen(true)
																				}
																			>
																				Predefined Blocks
																			</a>
																		)}
																	</Menu.Item>
																</Menu.Items>
															</Transition>
														</Menu>
													</td>
												</tr>

												{details?.map((item) => (
													<>
														<tr>
															<td className="max-w-0 px-0 py-5 align-top">
																<div className="truncate font-medium text-gray-900">
																	{item?.name}
																</div>
																<div className="truncate text-gray-500">{`S/N: ${item?.serialNumber}`}</div>
															</td>

															<td className="hidden py-5 pl-8 pr-0 align-top tabular-nums text-gray-700 sm:table-cell  whitespace-nowrap align-middle	">
																<div className="flex items-center">
																	<CheckCircleIcon className="w-5 h-5 text-green-600 mr-3" />
																	Completed on{' '}
																	{moment(item?.completedDate).format(
																		'MMM Do, YYYY',
																	)}
																</div>
															</td>
														</tr>
														{item?.type === 'repair' && (
															<>
																<tr>
																	<td colSpan={2}>
																		<div className="bg-red-50 py-4 px-6 rounded-md mb-4">
																			<div class="font-medium text-gray-900">
																				Repair
																			</div>
																			<div class="text-xs text-gray-500">
																				<span className="font-semibold">
																					Reported Issue:
																				</span>{' '}
																				{item?.reportedIssue}
																			</div>

																			<div className="mt-4 px-4 text-xs leading-6">
																				<div className="flex items-center">
																					{item?.fix}
																				</div>
																			</div>
																		</div>
																	</td>
																</tr>
															</>
														)}
														{item?.type === 'pm' && (
															<>
																<tr>
																	<td colSpan={2}>
																		<div className="bg-indigo-50 py-4 px-6 rounded-md mb-4">
																			<div class="font-medium text-gray-900">
																				Preventive Maintenance
																			</div>
																			<div class="text-xs text-gray-500">
																				<span className="font-semibold">
																					Reported Issue:
																				</span>{' '}
																				{item?.reportedIssue}
																			</div>

																			<div className="mt-4 px-4 text-xs leading-6">
																				<div className="flex items-center">
																					{item?.fix}
																				</div>
																			</div>
																		</div>
																	</td>
																</tr>
															</>
														)}
													</>
												))}
												{/*}
						<tr>
							<td colSpan={2}>
								<div className="bg-blue-50 py-4 px-6 rounded-md mb-4">
									<div class="font-medium text-gray-900">
										Preventive Maintenance
									</div>

									<div className="mt-4 px-4 text-xs leading-6">
										<div className="flex items-center">
											<CheckCircleIcon className="h-4 w-4 mr-3 text-green-500" />
											Inspected plastic trolley gears
										</div>
										<div className="flex items-center">
											<CheckCircleIcon className="h-4 w-4 mr-3 text-green-500" />
											Inspected trolley sensors
										</div>
										<div className="flex items-center">
											<CheckCircleIcon className="h-4 w-4 mr-3 text-green-500" />
											Applied grease where needed, back to OEM specifications
										</div>
										<div className="flex items-center">
											<CheckCircleIcon className="h-4 w-4 mr-3 text-green-500" />
											Checked for any visible wiring damage
										</div>
										<div className="flex items-center">
											<CheckCircleIcon className="h-4 w-4 mr-3 text-green-500" />
											Inspected all gears on lift/turn
										</div>
										<div className="flex items-center">
											<CheckCircleIcon className="h-4 w-4 mr-3 text-green-500" />
											Inspected shovel bearing
										</div>
										<div className="flex items-center">
											<CheckCircleIcon className="h-4 w-4 mr-3 text-green-500" />
											Inspected door and gaskets for misalignment
										</div>
										<div className="flex items-center">
											<CheckCircleIcon className="h-4 w-4 mr-3 text-green-500" />
											Inspected 1D/2D Scanner
										</div>
										<div className="flex items-center">
											<CheckCircleIcon className="h-4 w-4 mr-3 text-green-500" />
											Tubepicker has been removed, inspected, and preventive
											maintenance service performed.
										</div>
										<div className="flex items-center">
											<CheckCircleIcon className="h-4 w-4 mr-3 text-green-500" />
											Tested for functionality
										</div>
									</div>
									<div className="py-2 px-4 rounded-md mb-4 mt-4">
										<div class="text-gray-500 space-y-6">
											<p>
												User is seeing issues with the database when
												purging/consolidating the racks in the unit. After
												inpsecting the unit and running internal tests on the
												PC, it has been found that there is a mis-match in the
												unit's firmware and software. We have provided the user
												with a workaround to continue being able to
												purge/consolidate the system.
											</p>
										</div>
									</div>
								</div>
							</td>
								</tr>*/}
												{/*<tr>
							<td className="max-w-0 px-0 py-5 align-top">
								<div className="truncate font-medium text-gray-900">
									Precise Automation PF400 Arm
								</div>
								<div className="truncate text-gray-500">
									S/N: F0C-1903-9G-01456
								</div>
							</td>
							<td className="hidden py-5 pl-8 pr-0 align-top tabular-nums text-gray-700 sm:table-cell  whitespace-nowrap align-middle	">
								<div className="flex items-center">
									<CheckCircleIcon className="w-5 h-5 text-green-600 mr-3" />
									Completed on February 12th, 2024
								</div>
							</td>
						</tr>
						<tr>
							<td colSpan={2}>
								<div className="px-4 py-3 text-xs leading-6">
									<div className="bg-yellow-50 py-2 px-4 rounded-md mb-4">
										<div class="font-medium text-gray-900">
											Customer Reported
										</div>
										<div class="text-gray-500">
											The grippers are dropping plates intermittently
										</div>
									</div>

									<div className="py-2 px-4 rounded-md mb-4">
										<div class="font-medium text-gray-900">
											Preventive Maintanace & Repair
										</div>
										<div class="text-gray-500 space-y-6">
											<p>
												The robotic arm was tested to reproduce the issue
												reported by the customer. The plate grippers on the arm
												appeared to have been loose. They were removed,
												inspected, and reinstalled. This type of issue is
												generally seen when the arm crashes into a standstill
												position on the system.
											</p>

											<p>
												Next, the teaching positions of the system were off
												generally by a few millimeters, which would have caused
												inconsistant pick and place motions. This may also cause
												intermittent plate droppings. Without a teach plate
												present, a few nest positions were taught but the
												recommendation is to get a replacement Retisoft teach
												plate for this robot and re-teach all positions.
											</p>

											<p>
												Lastly, the preventive maintance service was completed.
												The internal belts of the arm have been re-tensioned and
												have met all appropriate OEM specifications. All motors
												and physical hardware was inspected. Unit was tested
												successfully
											</p>
										</div>
									</div>

								</div>
							</td>
								</tr>*/}

												{/*<tr>
							<td className="max-w-0 px-0 py-5 align-top">
								<div className="truncate font-medium text-gray-900">
									Thermo Multidrop Combi
								</div>
								<div className="truncate text-gray-500">
									{`S/N: 836-906 (Alias: Lady Gaga)`}
								</div>
							</td>

							<td className="hidden py-5 pl-8 pr-0 align-top tabular-nums text-gray-700 sm:table-cell  whitespace-nowrap align-middle	">
								<div className="flex items-center">
									<CheckCircleIcon className="w-5 h-5 text-green-600 mr-3" />
									Completed on April 4th, 2023
								</div>
							</td>
</tr>

<tr>
							<td colSpan={2}>
								<div className="bg-blue-50 py-4 px-6 rounded-md mb-4">
									<div class="font-medium text-gray-900">
										Preventive Maintenance
									</div>
									<div class="text-xs text-gray-500">
										<span className="font-semibold">Last Service:</span>{' '}
										Preventive Maintenance on April 2nd, 2023
									</div>

									<div className="mt-4 px-4 text-xs leading-6">
										<div className="flex items-center">
											<CheckCircleIcon className="h-4 w-4 mr-3 text-green-500" />
											Check all moving components
										</div>
										<div className="flex items-center">
											<CheckCircleIcon className="h-4 w-4 mr-3 text-green-500" />
											Apply grease to manufacturer specifications
										</div>
										<div className="flex items-center">
											<CheckCircleIcon className="h-4 w-4 mr-3 text-green-500" />
											Fully cleaned with 70% ethanol
										</div>
										<div className="flex items-center">
											<CheckCircleIcon className="h-4 w-4 mr-3 text-green-500" />
											Power supply voltage checked
										</div>
										<div className="flex items-center">
											<CheckCircleIcon className="h-4 w-4 mr-3 text-green-500" />
											Tested for functionality
										</div>
									</div>
								</div>

								<div className="bg-yellow-50 py-4 px-6 rounded-md mb-4">
									<div class="font-medium text-gray-900">
										Recommended: Power supply replacement
									</div>
									<div class="text-xs text-gray-500">
										Power supply looks to be original and is starting to show
										signs of wear. There are visible burn marks on the capacitor
										but still reading the correct voltage. We typically notice a
										number of issues occurring after this stage including
										insufficient power during shaking, slower/inconsistent
										dispensing, and cannot rule out a potential complete
										failure.
									</div>
								</div>
							</td>
						</tr>
						<tr>
							<td className="max-w-0 px-0 py-5 align-top">
								<div className="truncate font-medium text-gray-900">
									Thermo Multidrop Combi
								</div>
								<div className="truncate text-gray-500">{`S/N: 836-1244`}</div>
							</td>

							<td className="hidden py-5 pl-8 pr-0 align-top tabular-nums text-gray-700 sm:table-cell  whitespace-nowrap align-middle	">
								<div className="flex items-center">
									<CheckCircleIcon className="w-5 h-5 text-green-600 mr-3" />
									Completed on April 4th, 2023
								</div>
							</td>
						</tr>
						<tr>
							<td colSpan={2}>
								<div className="bg-blue-50 py-4 px-6 rounded-md mb-4">
									<div class="font-medium text-gray-900">
										Preventive Maintenance
									</div>
									<div class="text-xs text-gray-500">
										<span className="font-semibold">Last Service:</span>{' '}
										Preventive Maintenance on April 2nd, 2023
									</div>

									<div className="mt-4 px-4 text-xs leading-6">
										<div className="flex items-center">
											<CheckCircleIcon className="h-4 w-4 mr-3 text-green-500" />
											Check all moving components
										</div>
										<div className="flex items-center">
											<CheckCircleIcon className="h-4 w-4 mr-3 text-green-500" />
											Apply grease to manufacturer specifications
										</div>
										<div className="flex items-center">
											<CheckCircleIcon className="h-4 w-4 mr-3 text-green-500" />
											Fully cleaned with 70% ethanol
										</div>
										<div className="flex items-center">
											<CheckCircleIcon className="h-4 w-4 mr-3 text-green-500" />
											Power supply voltage checked
										</div>
										<div className="flex items-center">
											<CheckCircleIcon className="h-4 w-4 mr-3 text-green-500" />
											Tested for functionality
										</div>
									</div>
								</div>
							</td>
						</tr>
						<tr>
							<td className="max-w-0 px-0 py-5 align-top">
								<div className="truncate font-medium text-gray-900">
									Thermo Multidrop Combi
								</div>
								<div className="truncate text-gray-500">{`S/N: 836-1244`}</div>
							</td>

							<td className="hidden py-5 pl-8 pr-0 align-top tabular-nums text-gray-700 sm:table-cell  whitespace-nowrap align-middle	">
								<div className="flex items-center">
									<CheckCircleIcon className="w-5 h-5 text-green-600 mr-3" />
									Completed on April 4th, 2023
								</div>
							</td>
						</tr>
						<tr>
							<td colSpan={2}>
								<div className="bg-blue-50 py-4 px-6 rounded-md mb-4">
									<div class="font-medium text-gray-900">
										Preventive Maintenance
									</div>
									<div class="text-xs text-gray-500">
										<span className="font-semibold">Last Service:</span>{' '}
										Preventive Maintenance on April 2nd, 2023
									</div>

									<div className="mt-4 px-4 text-xs leading-6">
										<div className="flex items-center">
											<CheckCircleIcon className="h-4 w-4 mr-3 text-green-500" />
											Check all moving components
										</div>
										<div className="flex items-center">
											<CheckCircleIcon className="h-4 w-4 mr-3 text-green-500" />
											Apply grease to manufacturer specifications
										</div>
										<div className="flex items-center">
											<CheckCircleIcon className="h-4 w-4 mr-3 text-green-500" />
											Fully cleaned with 70% ethanol
										</div>
										<div className="flex items-center">
											<CheckCircleIcon className="h-4 w-4 mr-3 text-green-500" />
											Power supply voltage checked
										</div>
										<div className="flex items-center">
											<CheckCircleIcon className="h-4 w-4 mr-3 text-green-500" />
											Tested for functionality
										</div>
									</div>
								</div>
							</td>
						</tr>*/}

												{/*<tr>
							<td className="max-w-0 px-0 py-5 align-top">
								<div className="truncate font-medium text-gray-900">
									Liconic STT3K0
								</div>
								<div className="truncate text-gray-500">{`S/N: 4991`}</div>
							</td>

							<td className="hidden py-5 pl-8 pr-0 align-top tabular-nums text-gray-700 sm:table-cell  whitespace-nowrap align-middle	">
								<div className="flex items-center">
									<CheckCircleIcon className="w-5 h-5 text-green-600 mr-3" />
									Completed on September 21st, 2023
								</div>
							</td>
					</tr>*/}

												{/*<tr>
							<td className="max-w-0 px-0 py-5 align-top">
								<div className="truncate font-medium text-gray-900">
									Velocity11 PlateLoc
								</div>
								<div className="truncate text-gray-500">{`S/N: -`}</div>
							</td>

							<td className="hidden py-5 pl-8 pr-0 align-top tabular-nums text-gray-700 sm:table-cell  whitespace-nowrap align-middle	">
								<div className="flex items-center">
									<CheckCircleIcon className="w-5 h-5 text-green-600 mr-3" />
									Completed on September 7th, 2023
								</div>
							</td>
					</tr>*/}

												{/*<tr>
							<td colSpan={2}>
								<div className="bg-blue-50 py-4 px-6 rounded-md mb-4">
									<div class="font-medium text-gray-900">
										Preventive Maintenance
									</div>
									<div class="text-xs text-gray-500">
										<span className="font-semibold">Last Service:</span>{' '}
										Preventive Maintenance on April 2nd, 2023
									</div>

									<div className="mt-4 px-4 text-xs leading-6">
										<div className="flex items-center">
											<CheckCircleIcon className="h-4 w-4 mr-3 text-green-500" />
											Testing
										</div>
										<div className="flex items-center">
											<CheckCircleIcon className="h-4 w-4 mr-3 text-green-500" />
											Plug in
										</div>
									</div>
								</div>
							</td>
					</tr>*/}

												{/*<tr>
							<td className="max-w-0 px-0 py-5 align-top">
								<div className="truncate font-medium text-gray-900">
									Liconic STX220
								</div>
								<div className="truncate text-gray-500">{`S/N: 4233`}</div>
							</td>

							<td className="hidden py-5 pl-8 pr-0 align-top tabular-nums text-gray-700 sm:table-cell  whitespace-nowrap align-middle	">
								<div className="flex items-center">
									<CheckCircleIcon className="w-5 h-5 text-green-600 mr-3" />
									Completed on October 24th, 2023
								</div>
							</td>
						</tr>
						<tr>
							<td colSpan={2}>
								<div className="bg-red-50 py-4 px-6 rounded-md mb-4">
									<div class="font-medium text-gray-900">Repair</div>
									<div class="text-xs text-gray-500">
										<span className="font-semibold">Reported Issue:</span>{' '}
										Customer reports temperature is not holding properly and
										unit is alarming. The CO2 was reported to not be working
										properly
									</div>

									<div className="mt-4 px-4 text-xs leading-6">
										<div className="flex items-center">
											After an initial inspection of the unit, it was first
											noted that the CO2 was set to 0 in StoreX panel. Set back
											to 5.0% and tested. This seemed to have been the issue
											with the CO2.
											<br />
											<br />
											As far as the temperature not holding properly, it was
											noted on the previous service report that the gate motor
											was beginning to fail and highly recommended to be
											replaced. This was the first part that was tested and was
											malfunctioning. Gate motor was replaced and unit was
											tested.
										</div>
									</div>
								</div>
							</td>
				</tr>*/}
											</tbody>
										</table>

										{/*<ul
					role="list"
					className="grid grid-cols-2 gap-x-4 gap-y-8 sm:gap-x-6  xl:gap-x-8"
				>
					{[
						{
							source: RegeneronArm1,
						},
						{
							source: RegeneronArm2,
						},
						{
							source: RegeneronArm3,
						},
						{
							source: RegeneronArm4,
						},
					].map((file) => (
						<li key={file.source} className="relative">
							<div className="group aspect-h-7 aspect-w-10 block w-full overflow-hidden rounded-lg bg-gray-100 focus-within:ring-2 focus-within:ring-indigo-500 focus-within:ring-offset-2 focus-within:ring-offset-gray-100">
								<img
									src={file.source}
									alt=""
									className="pointer-events-none object-cover group-hover:opacity-75"
								/>
							</div>
						</li>
					))}
					</ul>*/}

										<table className="mt-16 w-fulltext-left text-sm leading-6">
											<colgroup>
												<col className="w-full" />
												<col />
												<col />
												<col />
											</colgroup>
											<thead className="border-b border-gray-200 text-gray-900">
												<tr>
													<th
														scope="col"
														className="px-0 py-3 font-semibold"
													></th>
													<th
														scope="col"
														className="hidden py-3 pl-8 pr-0 font-semibold sm:table-cell"
													></th>
												</tr>
											</thead>
											<tbody>
												{/*<tr>
							<td className="max-w-0 px-0 py-5 align-top">
								<div className="truncate font-medium text-gray-900">
									Precise Automation Linear Axis Rail
								</div>
								<div className="truncate text-gray-500">
									S/N: FXB-1903-4C-00276
								</div>
							</td>
							<td className="hidden py-5 pl-8 pr-0 align-top tabular-nums text-gray-700 sm:table-cell  whitespace-nowrap align-middle	">
								<div className="flex items-center">
									<CheckCircleIcon className="w-5 h-5 text-green-600 mr-3" />
									Completed on February 12th, 2024
								</div>
							</td>
					</tr>*/}
												{/*<tr>
							<td colSpan={2}>
								<div className="px-4 py-3 text-xs leading-6">

									<div className="py-2 px-4 rounded-md mb-4">
										<div class="font-medium text-gray-900">
											Preventive Maintanace
										</div>
										<div class="text-gray-500 space-y-6">
											<p>
												A preventive maintenance service on the axis rail was
												performed. All belts were tensioned and adjusted to OEM
												specifications. Motors and physical hardware were
												inspected. Unit was tested successfully.
											</p>
										</div>
									</div>
								</div>
							</td>
				</tr>*/}

												{/*<tr>
							<td className="max-w-0 px-0 py-5 align-top">
								<div className="truncate font-medium text-gray-900">
									Thermo Multidrop Combi
								</div>
								<div className="truncate text-gray-500">
									{`S/N: 836-906 (Alias: Lady Gaga)`}
								</div>
							</td>

							<td className="hidden py-5 pl-8 pr-0 align-top tabular-nums text-gray-700 sm:table-cell  whitespace-nowrap align-middle	">
								<div className="flex items-center">
									<CheckCircleIcon className="w-5 h-5 text-green-600 mr-3" />
									Completed on April 4th, 2023
								</div>
							</td>
</tr>

<tr>
							<td colSpan={2}>
								<div className="bg-blue-50 py-4 px-6 rounded-md mb-4">
									<div class="font-medium text-gray-900">
										Preventive Maintenance
									</div>
									<div class="text-xs text-gray-500">
										<span className="font-semibold">Last Service:</span>{' '}
										Preventive Maintenance on April 2nd, 2023
									</div>

									<div className="mt-4 px-4 text-xs leading-6">
										<div className="flex items-center">
											<CheckCircleIcon className="h-4 w-4 mr-3 text-green-500" />
											Check all moving components
										</div>
										<div className="flex items-center">
											<CheckCircleIcon className="h-4 w-4 mr-3 text-green-500" />
											Apply grease to manufacturer specifications
										</div>
										<div className="flex items-center">
											<CheckCircleIcon className="h-4 w-4 mr-3 text-green-500" />
											Fully cleaned with 70% ethanol
										</div>
										<div className="flex items-center">
											<CheckCircleIcon className="h-4 w-4 mr-3 text-green-500" />
											Power supply voltage checked
										</div>
										<div className="flex items-center">
											<CheckCircleIcon className="h-4 w-4 mr-3 text-green-500" />
											Tested for functionality
										</div>
									</div>
								</div>

								<div className="bg-yellow-50 py-4 px-6 rounded-md mb-4">
									<div class="font-medium text-gray-900">
										Recommended: Power supply replacement
									</div>
									<div class="text-xs text-gray-500">
										Power supply looks to be original and is starting to show
										signs of wear. There are visible burn marks on the capacitor
										but still reading the correct voltage. We typically notice a
										number of issues occurring after this stage including
										insufficient power during shaking, slower/inconsistent
										dispensing, and cannot rule out a potential complete
										failure.
									</div>
								</div>
							</td>
						</tr>
						<tr>
							<td className="max-w-0 px-0 py-5 align-top">
								<div className="truncate font-medium text-gray-900">
									Thermo Multidrop Combi
								</div>
								<div className="truncate text-gray-500">{`S/N: 836-1244`}</div>
							</td>

							<td className="hidden py-5 pl-8 pr-0 align-top tabular-nums text-gray-700 sm:table-cell  whitespace-nowrap align-middle	">
								<div className="flex items-center">
									<CheckCircleIcon className="w-5 h-5 text-green-600 mr-3" />
									Completed on April 4th, 2023
								</div>
							</td>
						</tr>
						<tr>
							<td colSpan={2}>
								<div className="bg-blue-50 py-4 px-6 rounded-md mb-4">
									<div class="font-medium text-gray-900">
										Preventive Maintenance
									</div>
									<div class="text-xs text-gray-500">
										<span className="font-semibold">Last Service:</span>{' '}
										Preventive Maintenance on April 2nd, 2023
									</div>

									<div className="mt-4 px-4 text-xs leading-6">
										<div className="flex items-center">
											<CheckCircleIcon className="h-4 w-4 mr-3 text-green-500" />
											Check all moving components
										</div>
										<div className="flex items-center">
											<CheckCircleIcon className="h-4 w-4 mr-3 text-green-500" />
											Apply grease to manufacturer specifications
										</div>
										<div className="flex items-center">
											<CheckCircleIcon className="h-4 w-4 mr-3 text-green-500" />
											Fully cleaned with 70% ethanol
										</div>
										<div className="flex items-center">
											<CheckCircleIcon className="h-4 w-4 mr-3 text-green-500" />
											Power supply voltage checked
										</div>
										<div className="flex items-center">
											<CheckCircleIcon className="h-4 w-4 mr-3 text-green-500" />
											Tested for functionality
										</div>
									</div>
								</div>
							</td>
						</tr>
						<tr>
							<td className="max-w-0 px-0 py-5 align-top">
								<div className="truncate font-medium text-gray-900">
									Thermo Multidrop Combi
								</div>
								<div className="truncate text-gray-500">{`S/N: 836-1244`}</div>
							</td>

							<td className="hidden py-5 pl-8 pr-0 align-top tabular-nums text-gray-700 sm:table-cell  whitespace-nowrap align-middle	">
								<div className="flex items-center">
									<CheckCircleIcon className="w-5 h-5 text-green-600 mr-3" />
									Completed on April 4th, 2023
								</div>
							</td>
						</tr>
						<tr>
							<td colSpan={2}>
								<div className="bg-blue-50 py-4 px-6 rounded-md mb-4">
									<div class="font-medium text-gray-900">
										Preventive Maintenance
									</div>
									<div class="text-xs text-gray-500">
										<span className="font-semibold">Last Service:</span>{' '}
										Preventive Maintenance on April 2nd, 2023
									</div>

									<div className="mt-4 px-4 text-xs leading-6">
										<div className="flex items-center">
											<CheckCircleIcon className="h-4 w-4 mr-3 text-green-500" />
											Check all moving components
										</div>
										<div className="flex items-center">
											<CheckCircleIcon className="h-4 w-4 mr-3 text-green-500" />
											Apply grease to manufacturer specifications
										</div>
										<div className="flex items-center">
											<CheckCircleIcon className="h-4 w-4 mr-3 text-green-500" />
											Fully cleaned with 70% ethanol
										</div>
										<div className="flex items-center">
											<CheckCircleIcon className="h-4 w-4 mr-3 text-green-500" />
											Power supply voltage checked
										</div>
										<div className="flex items-center">
											<CheckCircleIcon className="h-4 w-4 mr-3 text-green-500" />
											Tested for functionality
										</div>
									</div>
								</div>
							</td>
						</tr>*/}

												{/*<tr>
							<td className="max-w-0 px-0 py-5 align-top">
								<div className="truncate font-medium text-gray-900">
									Liconic STT3K0
								</div>
								<div className="truncate text-gray-500">{`S/N: 4991`}</div>
							</td>

							<td className="hidden py-5 pl-8 pr-0 align-top tabular-nums text-gray-700 sm:table-cell  whitespace-nowrap align-middle	">
								<div className="flex items-center">
									<CheckCircleIcon className="w-5 h-5 text-green-600 mr-3" />
									Completed on September 21st, 2023
								</div>
							</td>
					</tr>*/}

												{/*<tr>
							<td className="max-w-0 px-0 py-5 align-top">
								<div className="truncate font-medium text-gray-900">
									Velocity11 PlateLoc
								</div>
								<div className="truncate text-gray-500">{`S/N: -`}</div>
							</td>

							<td className="hidden py-5 pl-8 pr-0 align-top tabular-nums text-gray-700 sm:table-cell  whitespace-nowrap align-middle	">
								<div className="flex items-center">
									<CheckCircleIcon className="w-5 h-5 text-green-600 mr-3" />
									Completed on September 7th, 2023
								</div>
							</td>
					</tr>*/}

												{/*<tr>
							<td colSpan={2}>
								<div className="bg-blue-50 py-4 px-6 rounded-md mb-4">
									<div class="font-medium text-gray-900">
										Preventive Maintenance
									</div>
									<div class="text-xs text-gray-500">
										<span className="font-semibold">Last Service:</span>{' '}
										Preventive Maintenance on April 2nd, 2023
									</div>

									<div className="mt-4 px-4 text-xs leading-6">
										<div className="flex items-center">
											<CheckCircleIcon className="h-4 w-4 mr-3 text-green-500" />
											Testing
										</div>
										<div className="flex items-center">
											<CheckCircleIcon className="h-4 w-4 mr-3 text-green-500" />
											Plug in
										</div>
									</div>
								</div>
							</td>
					</tr>*/}

												{/*<tr>
							<td className="max-w-0 px-0 py-5 align-top">
								<div className="truncate font-medium text-gray-900">
									Liconic STX220
								</div>
								<div className="truncate text-gray-500">{`S/N: 4233`}</div>
							</td>

							<td className="hidden py-5 pl-8 pr-0 align-top tabular-nums text-gray-700 sm:table-cell  whitespace-nowrap align-middle	">
								<div className="flex items-center">
									<CheckCircleIcon className="w-5 h-5 text-green-600 mr-3" />
									Completed on October 24th, 2023
								</div>
							</td>
						</tr>
						<tr>
							<td colSpan={2}>
								<div className="bg-red-50 py-4 px-6 rounded-md mb-4">
									<div class="font-medium text-gray-900">Repair</div>
									<div class="text-xs text-gray-500">
										<span className="font-semibold">Reported Issue:</span>{' '}
										Customer reports temperature is not holding properly and
										unit is alarming. The CO2 was reported to not be working
										properly
									</div>

									<div className="mt-4 px-4 text-xs leading-6">
										<div className="flex items-center">
											After an initial inspection of the unit, it was first
											noted that the CO2 was set to 0 in StoreX panel. Set back
											to 5.0% and tested. This seemed to have been the issue
											with the CO2.
											<br />
											<br />
											As far as the temperature not holding properly, it was
											noted on the previous service report that the gate motor
											was beginning to fail and highly recommended to be
											replaced. This was the first part that was tested and was
											malfunctioning. Gate motor was replaced and unit was
											tested.
										</div>
									</div>
								</div>
							</td>
				</tr>*/}
											</tbody>
										</table>

										{/* Photos Section 
										<div className="mt-8">
											<div className="flex items-center justify-between mb-4">
												<h3 className="text-sm font-medium text-gray-900">
													Photos
												</h3>
												<div data-html2canvas-ignore={true}>
													<input
														type="file"
														ref={fileInputRef}
														onChange={handleImageUpload}
														accept="image/*"
														multiple
														className="hidden"
													/>
													<button
														type="button"
														onClick={() => fileInputRef.current?.click()}
														disabled={isUploadingImages === 'pending'}
														className="inline-flex items-center gap-x-1.5 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50"
													>
														<PhotoIcon className="-ml-0.5 h-5 w-5" />
														{isUploadingImages === 'pending'
															? 'Uploading...'
															: 'Upload Photos'}
													</button>
												</div>
											</div>
											<ul
												role="list"
												className="grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-2 sm:gap-x-6 lg:grid-cols-2 xl:gap-x-8"
											>
												{images.map((image, index) => (
													<li
														key={image.key || index}
														className="relative group"
													>
														<div className="aspect-h-7 aspect-w-10 block w-full overflow-hidden rounded-lg bg-gray-100 focus-within:ring-2 focus-within:ring-indigo-500 focus-within:ring-offset-2 focus-within:ring-offset-gray-100">
															<img
																src={image.url}
																alt={image.name || `Photo ${index + 1}`}
																className="pointer-events-none object-cover group-hover:opacity-75"
															/>
														</div>
														<button
															type="button"
															onClick={() => handleRemoveImage(index)}
															data-html2canvas-ignore={true}
															className="absolute top-2 right-2 rounded-full bg-red-600 p-1 text-white shadow-sm hover:bg-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
														>
															<TrashIcon className="h-4 w-4" />
														</button>
													</li>
												))}
											</ul>
											{images.length === 0 && (
												<div
													data-html2canvas-ignore={true}
													className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg"
												>
													<PhotoIcon className="mx-auto h-12 w-12 text-gray-400" />
													<p className="mt-2 text-sm text-gray-500">
														No photos uploaded yet
													</p>
													<p className="text-xs text-gray-400">
														Click "Upload Photos" to add images
													</p>
												</div>
											)}
										</div>*/}

										<div className="flex justify-center items-center text-center mt-10">
											<div>
												<div className="text-gray-700 font-semibold text-xs">
													Generated by Scipods
												</div>
												{/*<div className="text-xxs font-light text-gray-600">
							www.scipods.com/r/----
					</div>*/}

												<img
													src="https://cloud.scipods.com/images/1e77163140de92f0cde1d3d1b99ba082.png"
													className="h-10 w-10 m-auto my-4"
												/>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</>
				)}

				<Modal
					title="Add block"
					open={isAddContentBlockOpen}
					onClose={() => setAddContentBlockOpen(false)}
				>
					<div className="grid grid-cols-2 gap-y-4">
						<TemplateCreateable
							name="Type"
							options={BLOCK_TYPES}
							onChange={(index, type, raw) => {
								setContentBlockForm({ ...contentBlockForm, type: raw });
							}}
							value={contentBlockForm?.type?.value}
						/>

						{contentBlockForm?.type?.value === 'repair' && (
							<>
								<TemplateEditor
									name="Reported Issue"
									value={contentBlockForm?.issue?.delta}
									onChange={(index, issue, raw) =>
										setContentBlockForm({ ...contentBlockForm, issue: raw })
									}
									quillClassName="h-36 mb-2"
									toolbar={false}
								/>
								<TemplateEditor
									name="Solution"
									value={contentBlockForm?.solution?.delta}
									onChange={(index, solution, raw) =>
										setContentBlockForm({ ...contentBlockForm, solution: raw })
									}
									quillClassName="h-36 mb-2"
									toolbar={false}
								/>
							</>
						)}

						{contentBlockForm?.type?.value === 'pm' && (
							<>
								<TemplateEditor
									name="Content"
									value={contentBlockForm?.pm?.delta}
									onChange={(index, solution, raw) =>
										setContentBlockForm({ ...contentBlockForm, pm: raw })
									}
									quillClassName="h-36 mb-2"
									toolbar={false}
								/>
							</>
						)}

						{contentBlockForm?.type?.value === 'notes' && (
							<>
								<TemplateEditor
									name="Content"
									value={contentBlockForm?.notes?.delta}
									onChange={(index, solution, raw) =>
										setContentBlockForm({ ...contentBlockForm, notes: raw })
									}
									quillClassName="h-36 mb-2"
									toolbar={false}
								/>
							</>
						)}

						{contentBlockForm?.type?.value === 'parts' && (
							<>
								<TemplateEditor
									name="Content"
									value={contentBlockForm?.parts?.delta}
									onChange={(index, solution, raw) =>
										setContentBlockForm({ ...contentBlockForm, parts: raw })
									}
									quillClassName="h-36 mb-2"
									toolbar={false}
								/>
							</>
						)}

						<div className="flex justify-end col-span-2">
							<Button
								onClick={() =>
									onCreateServiceBlock(
										{
											serviceReportId: serviceReport?.id,
											blocks: [
												...(contentBlockForm?.issue
													? [
															{
																name: 'Reported Issue',
																content: contentBlockForm?.issue?.content,
																delta: contentBlockForm?.issue?.delta,
																type: 'repair',
															},
														]
													: []),
												...(contentBlockForm?.solution
													? [
															{
																name: 'Solution',
																content: contentBlockForm?.solution?.content,
																delta: contentBlockForm?.solution?.delta,
																type: 'solution',
															},
														]
													: []),
												...(contentBlockForm?.pm
													? [
															{
																name: 'Preventive Maintenance',
																content: contentBlockForm?.pm?.content,
																delta: contentBlockForm?.pm?.delta,
																type: 'pm',
															},
														]
													: []),
												...(contentBlockForm?.notes
													? [
															{
																name: 'Notes',
																content: contentBlockForm?.notes?.content,
																delta: contentBlockForm?.notes?.delta,
																type: 'notes',
															},
														]
													: []),
												...(contentBlockForm?.parts
													? [
															{
																name: 'Parts Replaced',
																content: contentBlockForm?.parts?.content,
																delta: contentBlockForm?.parts?.delta,
																type: 'parts',
															},
														]
													: []),
											],
										},
										() => setAddContentBlockOpen(false),
									)
								}
							>
								Add Block(s)
							</Button>
						</div>
					</div>
				</Modal>
				<Modal
					title="Add a predefined content block"
					open={isAddPredefinedContentBlockOpen}
					onClose={() => setAddPredefinedContentBlockOpen(false)}
				>
					<div className="grid grid-cols-2 gap-y-4">
						<TemplateReactSelect
							name="Block"
							options={serviceBlocks?.map((block) => ({
								label: block?.name,
								value: block?.id,
								block,
							}))}
							onChange={(index, selectedBlock) => {
								setAddPredefinedContentBlockOpen(false);
								setAddContentBlockOpen({
									id: selectedBlock?.value,
									serviceReportId: serviceReport?.id,
								});
								setContentBlockForm({
									...contentBlockForm,
									type: { value: selectedBlock?.block?.type },
									pm: {
										content: selectedBlock?.block?.content,
										delta: selectedBlock?.block?.delta,
									},
								});
							}}
						/>
					</div>{' '}
				</Modal>
			</MainContent>
		</>
	);

	/*return (
		<MainContent>
			<PageHeading divider={false}>Title goes here</PageHeading>
			<div>Single Report</div>

            
		</MainContent>
	);*/
};

const ServiceReportsBeta = () => {
	return (
		<Routes>
			<ProtectedRoute
				exact
				path="/admin/service-reports"
				component={ServiceReportsList}
			/>
			<ProtectedRoute
				exact
				path="/admin/service-report/:id"
				component={ServiceReportsSingle}
			/>
		</Routes>
	);
};

export { ServiceReportsSingle };
export default ServiceReportsBeta;
